// Ravencoin Asset JSON Parser for VPS RPC Response
// Parses the live blockchain response from VPS Ravencoin node

import { RavencoinAsset } from '../backend';

export interface RavencoinRPCResponse {
  jsonrpc: string;
  result: Record<string, RavencoinAssetData>;
  error?: {
    code: number;
    message: string;
  };
  id: number;
}

export interface RavencoinAssetData {
  name: string;
  amount: number;
  units: number;
  reissuable: number;
  has_ipfs: number;
  ipfs_hash?: string;
  issuer?: string;
  block_height: number;
  blockhash: string;
}

/**
 * Validates if a string is a valid Ravencoin asset name
 * Asset names must be 3-30 characters, uppercase A-Z, 0-9, underscore, period
 */
export function isValidAssetName(name: string): boolean {
  if (!name || name.length < 3 || name.length > 30) {
    return false;
  }
  
  // Ravencoin asset naming rules
  const validPattern = /^[A-Z0-9_.]+$/;
  return validPattern.test(name);
}

/**
 * Validates if a string is a valid Ravencoin address
 * Ravencoin addresses start with 'R' and are 34 characters long
 */
export function isValidRavencoinAddress(address: string): boolean {
  if (!address || address.length !== 34 || !address.startsWith('R')) {
    return false;
  }
  
  // Check for placeholder patterns
  const placeholderPatterns = ['PLACEHOLDER', 'PROXY', 'CREATOR', 'ADDRESS'];
  for (const pattern of placeholderPatterns) {
    if (address.includes(pattern)) {
      return false;
    }
  }
  
  // Check for repeated characters (likely placeholder)
  if (/^R(.)\1{32}$/.test(address)) {
    return false;
  }
  
  return true;
}

/**
 * Parses the Ravencoin RPC response from VPS node
 * Converts blockchain asset data into frontend-compatible format
 */
export function parseRavencoinAssetsFromResponse(responseText: string): RavencoinAsset[] {
  const assets: RavencoinAsset[] = [];
  
  try {
    // Parse JSON response
    const response: RavencoinRPCResponse = JSON.parse(responseText);
    
    // Check for RPC errors
    if (response.error) {
      // Only log error message, not full error object
      const errorMsg = response.error?.message || 'Unknown RPC error';
      console.error('Ravencoin RPC Error:', errorMsg);
      throw new Error(`RPC Error: ${errorMsg}`);
    }
    
    // Validate response structure
    if (!response.result || typeof response.result !== 'object') {
      // Don't log entire response object - it's huge (thousands of assets)
      console.error('Invalid RPC response structure: missing result object');
      throw new Error('Invalid response structure: missing result object');
    }
    
    // Parse each asset from the result object
    const assetNames = Object.keys(response.result);
    // Only log in development - this can be thousands of assets
    if (import.meta.env.DEV) {
      console.log(`📊 Parsing ${assetNames.length} assets from blockchain response`);
    }
    
    for (const assetName of assetNames) {
      try {
        // Validate asset name
        if (!isValidAssetName(assetName)) {
          // Only log in development - thousands of assets might have invalid names
          if (import.meta.env.DEV) {
            console.warn(`⚠️ Skipping invalid asset name: ${assetName}`);
          }
          continue;
        }
        
        const assetData = response.result[assetName];
        
        // Validate asset data structure
        if (!assetData || typeof assetData !== 'object') {
          // Only log in development
          if (import.meta.env.DEV) {
            console.warn(`⚠️ Skipping asset with invalid data: ${assetName}`);
          }
          continue;
        }
        
        // Extract quantity (amount * 10^-units for decimal representation)
        const rawAmount = assetData.amount || 0;
        const units = assetData.units || 0;
        const quantity = rawAmount / Math.pow(10, units);
        
        // Validate quantity
        if (quantity < 0 || !isFinite(quantity)) {
          // Only log in development
          if (import.meta.env.DEV) {
            console.warn(`⚠️ Skipping asset with invalid quantity: ${assetName}`);
          }
          continue;
        }
        
        // Extract IPFS metadata if available
        let metadata: string | undefined;
        if (assetData.has_ipfs === 1 && assetData.ipfs_hash) {
          metadata = `IPFS: ${assetData.ipfs_hash}`;
        }
        
        // Extract creator (issuer) address
        const creator = assetData.issuer && assetData.issuer.startsWith('R') && assetData.issuer.length === 34
          ? assetData.issuer
          : 'RUnknownCreatorAddress123456789012';
        
        // Detect NFT: has IPFS metadata AND (quantity <= 1.0 OR not reissuable)
        const hasMetadata = !!metadata;
        const reissuable = assetData.reissuable === 1;
        const isNFT = hasMetadata && (quantity <= 1.0 || !reissuable);
        
        // Create asset object
        const asset: RavencoinAsset = {
          assetName,
          creator,
          quantity,
          metadata: metadata || undefined,
          isNFT,
        };
        
        assets.push(asset);
      } catch (assetError) {
        // Only log error message in development, not full error object
        if (import.meta.env.DEV) {
          const errorMsg = assetError instanceof Error ? assetError.message : String(assetError);
          console.error(`❌ Failed to parse asset ${assetName}:`, errorMsg);
        }
        // Continue processing other assets
      }
    }
    
    // Only log in development
    if (import.meta.env.DEV) {
      console.log(`✅ Successfully parsed ${assets.length} valid assets`);
    }
    return assets;
    
  } catch (error) {
    // Don't log full error object - it might contain huge response data
    // Only log error message
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Failed to parse Ravencoin RPC response:', errorMessage);
    throw new Error(`JSON parsing failed: ${errorMessage}`);
  }
}

/**
 * Validates the entire RPC response before parsing
 */
export function validateRPCResponse(responseText: string): {
  valid: boolean;
  error?: string;
} {
  if (!responseText || responseText.trim().length === 0) {
    return { valid: false, error: 'Empty response' };
  }
  
  try {
    const response = JSON.parse(responseText);
    
    if (response.error) {
      return { valid: false, error: `RPC Error: ${response.error.message}` };
    }
    
    if (!response.result) {
      return { valid: false, error: 'Missing result object' };
    }
    
    if (typeof response.result !== 'object') {
      return { valid: false, error: 'Result is not an object' };
    }
    
    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      error: `JSON parse error: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
}

/**
 * Extracts summary statistics from parsed assets
 */
export function getAssetStatistics(assets: RavencoinAsset[]): {
  totalAssets: number;
  totalQuantity: number;
  assetsWithMetadata: number;
  uniqueCreators: number;
} {
  const uniqueCreators = new Set(assets.map(a => a.creator));
  
  return {
    totalAssets: assets.length,
    totalQuantity: assets.reduce((sum, asset) => sum + asset.quantity, 0),
    assetsWithMetadata: assets.filter(a => a.metadata).length,
    uniqueCreators: uniqueCreators.size,
  };
}
